import { useState, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Link2, Settings2 } from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessWastePage, canAccessCostPriceAutomation } from '../../../services/accessManagement/accessManagement';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  baseTextSize,
  cardClass,
  borderPrimary,
} from '../../../styling/stylingHelper';
import { TopSection } from '../../ui/TopSection';
import { TabbedSections } from '../../ui/TabbedSections';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useWasteStore } from '../../../stores/appWasteStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useFiltersStore, OverallTypeFilter } from '../../../stores/appFiltersStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
  useLoadWaste,
} from '../../../utils/dataLoadingUtils';
import { enrichCostPriceListWithCurrentPrices, createCostPriceFromPlaceholder } from '../../../utils/costPriceUtils';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { resolveCostTargets } from '../../../utils/costTargets';
import { useByRestaurantCostData } from '../../../hooks/useByRestaurantCostData';
import {
  computeWasteByProduct,
  EMPTY_WASTE_BY_PRODUCT,
  needsWasteAttention,
  matchWasteDish,
  WASTE_REASON_LABELS,
  formatWasteQuantity,
} from '../../../utils/reports/wasteByProduct';
import { WasteEntry } from '../../../services/waste/wasteService';
import { createCostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import CostPriceDetailModal from '../costPrice/new/CostPrice/CostPriceDetailModal';
import LogWasteModal from './LogWasteModal';
import LinkWasteEntryModal from './LinkWasteEntryModal';
import WasteLinkModal from './WasteLinkModal';
import WasteByProductTable from './WasteByProductTable';
import {
  WasteLinkBadge,
  useDishMatchIndex,
  useWasteProductOptions,
} from './wasteLinkShared';

/** The page's three views. All three read the SAME date range from `TopSection`. */
type WasteTab = 'reported' | 'byProduct' | 'needsAttention';

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const WasteTrackingPage = () => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const hasAccess = canAccessWastePage(backendUser, accountSettings);
  // Bulk link automation (AI dry-run) rides the cost_price_automation account feature —
  // force-on while impersonating; manual single-entry linking stays open to everyone.
  const canUseLinkAutomation = canAccessCostPriceAutomation(backendUser, accountSettings);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const showExVat = useAppUserDefaultStore(s => s.showExVatOrders);

  // The by-product view needs the same three datasets the utilization report walks.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();
  useLoadWaste();

  const wasteList = useWasteStore(s => s.wasteList);
  const isLoading = useWasteStore(s => s.isLoading);
  const deleteWaste = useWasteStore(s => s.delete);
  const orders = useOrderHistory(s => s.orderHistoryList);
  const revenue = useRevenueHistory(s => s.revenueHistoryList);
  const costPriceList = useCostPriceStore(s => s.costPriceList);
  const customIngredients = useCostPriceStore(s => s.customIngredients);
  // Whole-store handles (not slices): the shared cost-price create helper takes them as
  // collaborators, exactly as the cost-price page passes them.
  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();
  const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
  const reportSettings = useRestaurantStore(s => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);

  const { dateRange, setDateRange, selectedTimeOption, setSelectedTimeOption } = useTimeControls();

  const [tab, setTab] = useState<WasteTab>('reported');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WasteEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<WasteEntry | null>(null);
  const [linkingEntry, setLinkingEntry] = useState<WasteEntry | null>(null);
  const [isBulkLinkOpen, setIsBulkLinkOpen] = useState(false);
  /** Waste id whose cost price is being created (button spinner), and the analysis to open. */
  const [settingUpDish, setSettingUpDish] = useState<string | null>(null);
  const [setupCostPriceId, setSetupCostPriceId] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Reset filter to 'all' if not applicable
  useEffect(() => {
    const available = ['all', 'dish', 'ingredient'];
    if (overallTypeFilter && !available.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Filter by date range and waste type
  const filteredWaste = useMemo(() => {
    let entries = wasteList;

    // Filter by date range (date-only comparison using local dates, ignore time)
    if (dateRange) {
      const toLocalDateStr = (d: Date) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const startStr = toLocalDateStr(dateRange.start);
      const endStr = toLocalDateStr(dateRange.end);
      entries = entries.filter(e => {
        const dStr = toLocalDateStr(new Date(e.wasteDate));
        return dStr >= startStr && dStr <= endStr;
      });
    }

    // Filter by waste type
    if (overallTypeFilter && overallTypeFilter !== 'all') {
      entries = entries.filter(e => e.wasteType === overallTypeFilter);
    }

    return entries;
  }, [wasteList, dateRange, overallTypeFilter]);

  const productOptions = useWasteProductOptions(orders);
  // Dish identity is DERIVED from the entry name — nothing about it is stored. Rebuilding
  // this map is what makes a dish auto-link the moment its cost price is set up.
  const dishIndex = useDishMatchIndex(costPriceList);

  // Two kinds, two different fixes: an unlinked ingredient needs LINKING, an unmatched dish
  // needs its cost price SET UP.
  const needsAttentionInPeriod = useMemo(
    () => filteredWaste.filter(e => needsWasteAttention(e, dishIndex)),
    [filteredWaste, dishIndex]
  );
  const dishesNeedingSetup = useMemo(
    () => needsAttentionInPeriod.filter(e => e.wasteType === 'dish'),
    [needsAttentionInPeriod]
  );

  // The bulk automation runs server-side over EVERY unlinked INGREDIENT, not just the
  // period's — so its count comes from the full list.
  const unlinkedIngredientsAllTime = useMemo(
    () => wasteList.filter(e => e.wasteType === 'ingredient' && e.linkStatus === 'unlinked').length,
    [wasteList]
  );

  const defaultWastePercentage = useMemo(
    () => resolveWastePercentage(reportSettings?.wastePercentage),
    [reportSettings]
  );
  const costTargets = useMemo(() => resolveCostTargets(reportSettings?.costTargets), [reportSettings]);
  const byRestaurant = useByRestaurantCostData(costPriceList, restaurantId ?? '');

  // Only the by-product view needs the (expensive) enrichment + explosion.
  const enriched = useMemo(
    () =>
      tab === 'byProduct'
        ? enrichCostPriceListWithCurrentPrices(costPriceList, orders, {
            customIngredients,
            showExVat,
            allSubrecipes: subrecipesById,
            revenueHistory: revenue,
            byRestaurant,
            currentRestaurantId: restaurantId ?? '',
            defaultWastePercentage,
          })
        : [],
    [tab, costPriceList, orders, customIngredients, showExVat, subrecipesById, revenue, byRestaurant, restaurantId, defaultWastePercentage]
  );

  // Gated on the tab like `enriched` above: without the cost prices the roll-up can only
  // report every linked dish as "cost price not found", so it must not run at all.
  const byProduct = useMemo(
    () =>
      tab === 'byProduct'
        ? computeWasteByProduct({
            wasteEntries: filteredWaste,
            orders,
            enriched,
            subrecipesById,
            defaultWastePercentage,
            currentRestaurantId: restaurantId ?? '',
          })
        : EMPTY_WASTE_BY_PRODUCT,
    [tab, filteredWaste, orders, enriched, subrecipesById, defaultWastePercentage, restaurantId]
  );

  /**
   * "Set up cost price" for a dish that has no analysis yet. Same create-then-open path the
   * cost-price page uses for a revenue-item placeholder (`createCostPriceFromPlaceholder` →
   * open `CostPriceDetailModal`), reused rather than reimplemented. The dish name comes from
   * the revenue-item picker, so it goes into BOTH `costPriceName` and `menuItemName` — which
   * is exactly what makes the entry auto-link once the store reloads.
   */
  const handleSetupDish = useCallback(async (entry: WasteEntry) => {
    if (!restaurantId || settingUpDish) return;
    // Re-check at click time: a sibling row with the same name may have just created it.
    const existing = matchWasteDish(entry.itemName, dishIndex);
    if (existing) {
      setSetupCostPriceId(existing.id);
      return;
    }
    setSettingUpDish(entry.id);
    setSetupError(null);
    try {
      const created = await createCostPriceFromPlaceholder(
        { costPriceName: entry.itemName, menuItemName: entry.itemName, restaurantId },
        categoriesStore,
        costPriceStore,
        createCostPriceAnalysis
      );
      setSetupCostPriceId(created.id);
    } catch (err) {
      console.error('Failed to create cost price for waste dish', err);
      setSetupError(entry.id);
    } finally {
      setSettingUpDish(null);
    }
  }, [restaurantId, settingUpDish, dishIndex, categoriesStore, costPriceStore]);

  const handleDelete = useCallback(async () => {
    if (!deletingEntry) return;
    try {
      await deleteWaste(deletingEntry.id);
    } catch (err) {
      console.error('Failed to delete waste entry', err);
    }
    setDeletingEntry(null);
  }, [deletingEntry, deleteWaste]);

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  if (isAllRestaurants) {
    return (
      <div className="p-6">
        <p className={cn(baseTextSize, secondaryText)}>
          {t('waste.selectRestaurant')}
        </p>
      </div>
    );
  }

  const renderEntryCard = (entry: WasteEntry) => (
    <div
      key={entry.id}
      className={cn(cardClass, borderPrimary, "border rounded-xl p-4 flex items-center justify-between gap-4")}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={cn(semiBoldText, primaryText, baseTextSize)}>
            {entry.itemName}
          </span>
          <span className={cn(
            "inline-block px-2 py-0.5 rounded-full text-xs font-medium",
            entry.wasteType === 'dish'
              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
              : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          )}>
            {entry.wasteType === 'dish' ? t('waste.typeDish') : t('waste.typeIngredient')}
          </span>
          <WasteLinkBadge entry={entry} dishIndex={dishIndex} />
        </div>
        <div className={cn("flex items-center gap-4 flex-wrap", smallTextSize, tertiaryText)}>
          <span>{formatDate(new Date(entry.wasteDate))}</span>
          <span className={cn(semiBoldText, secondaryText)}>
            {formatWasteQuantity(entry.quantity, entry.unit)}
          </span>
          {entry.reason && (
            <span>
              {t(WASTE_REASON_LABELS[entry.reason] || entry.reason)}
              {entry.reason === 'other' && entry.reasonNote && `: ${entry.reasonNote}`}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* A dish is never hand-linked: it either matches a cost price by name or needs one
            set up. Only ingredients get the link drawer. */}
        {entry.wasteType === 'dish' ? (
          !matchWasteDish(entry.itemName, dishIndex) && (
            <button
              onClick={() => handleSetupDish(entry)}
              disabled={settingUpDish === entry.id}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition disabled:opacity-60',
                borderPrimary,
                smallTextSize,
                semiBoldText,
                secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              title={t('waste.setup.action') as string}
            >
              <Settings2 className="w-4 h-4" />
              {settingUpDish === entry.id ? t('waste.setup.creating') : t('waste.setup.action')}
            </button>
          )
        ) : (
          <button
            onClick={() => setLinkingEntry(entry)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            aria-label={t('waste.link.editLink') as string}
            title={t('waste.link.editLink') as string}
          >
            <Link2 className="w-4 h-4 text-gray-500 dark:text-textTertiary-dark" />
          </button>
        )}
        <button
          onClick={() => setEditingEntry(entry)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          aria-label={t('waste.editWaste') as string}
        >
          <Edit className="w-4 h-4 text-gray-500 dark:text-textTertiary-dark" />
        </button>
        <button
          onClick={() => setDeletingEntry(entry)}
          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          aria-label={t('waste.deleteWaste') as string}
        >
          <Trash2 className="w-4 h-4 text-red-500 dark:text-textRed-dark" />
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <MobilePageHeader title={t('nav.wasteTracking')} right={<RestaurantSelector />} />

      <TopSection
        title={t('nav.wasteTracking')}
        backButton={false}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        overallTypeOptions={['all', 'dish', 'ingredient']}
        onOverallTypeChange={(v: string) => setOverallTypeFilter(v as OverallTypeFilter)}
        currentOverallTypeValue={overallTypeFilter || 'all'}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
        <TabbedSections
          options={[
            { value: 'reported', label: t('waste.tabReported') },
            { value: 'byProduct', label: t('waste.tabByProduct') },
            { value: 'needsAttention', label: t('waste.tabNeedsAttention') },
          ]}
          value={tab}
          onChange={(v) => setTab(v as WasteTab)}
          isTimeOptionSelection={false}
          maxTabsBeforeDropdown={3}
        />
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-xl text-white dark:text-white bg-primary dark:bg-primary hover:bg-primary/90 transition self-end sm:self-auto",
            smallTextSize,
            semiBoldText
          )}
        >
          <Plus className="w-4 h-4" />
          {t('waste.logNewWaste')}
        </button>
      </div>

      {/* Setup can be triggered from any tab showing entry cards, so its error lives here
          rather than inside one tab's branch. */}
      {setupError && (
        <div className="mb-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className={cn(smallTextSize, 'text-red-700 dark:text-red-300')}>{t('waste.setup.error')}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-primary" />
        </div>
      ) : tab === 'byProduct' ? (
        <WasteByProductTable data={byProduct} periodEntryCount={filteredWaste.length} />
      ) : tab === 'needsAttention' ? (
        <div className="space-y-3">
          <div className={cn(cardClass, borderPrimary, 'border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3')}>
            <div>
              <p className={cn(smallTextSize, semiBoldText, primaryText)}>
                {t('waste.needsAttention.count', { n: needsAttentionInPeriod.length })}
              </p>
              <p className={cn('mt-1 text-[12px]', tertiaryText)}>
                {t('waste.needsAttention.allTimeNote', { n: unlinkedIngredientsAllTime })}
              </p>
              {dishesNeedingSetup.length > 0 && (
                <p className={cn('mt-1 text-[12px]', tertiaryText)}>
                  {t('waste.needsAttention.dishesNote', { n: dishesNeedingSetup.length })}
                </p>
              )}
            </div>
            {canUseLinkAutomation && (
              <button
                onClick={() => setIsBulkLinkOpen(true)}
                disabled={unlinkedIngredientsAllTime === 0}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-xl text-white dark:text-white bg-darkGreenColor dark:bg-darkGreenColor-dark hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition',
                  smallTextSize,
                  semiBoldText
                )}
              >
                <Link2 className="w-4 h-4" />
                {t('waste.needsAttention.bulkButton')}
              </button>
            )}
          </div>
          {needsAttentionInPeriod.length === 0 ? (
            <div className="text-center py-12">
              <p className={cn(baseTextSize, tertiaryText)}>{t('waste.needsAttention.empty')}</p>
            </div>
          ) : (
            <div className="space-y-2">{needsAttentionInPeriod.map(renderEntryCard)}</div>
          )}
        </div>
      ) : filteredWaste.length === 0 ? (
        <div className="text-center py-12">
          <p className={cn(baseTextSize, tertiaryText)}>{t('waste.noEntries')}</p>
        </div>
      ) : (
        <div className="space-y-2">{filteredWaste.map(renderEntryCard)}</div>
      )}

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || editingEntry) && restaurantId && (
        <LogWasteModal
          isOpen={true}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingEntry(null);
          }}
          mode={editingEntry ? 'edit' : 'create'}
          existingEntry={editingEntry || undefined}
          restaurantId={restaurantId}
        />
      )}

      {/* Single-entry link drawer (ingredients only) */}
      <LinkWasteEntryModal
        entry={linkingEntry}
        productOptions={productOptions}
        onClose={() => setLinkingEntry(null)}
      />

      {/* Bulk link automation */}
      {restaurantId && canUseLinkAutomation && (
        <WasteLinkModal
          isOpen={isBulkLinkOpen}
          onClose={() => setIsBulkLinkOpen(false)}
          restaurantId={restaurantId}
          pendingCount={unlinkedIngredientsAllTime}
          productOptions={productOptions}
        />
      )}

      {/* Cost-price editor for a dish that had none — on close the store already holds the
          new analysis, so the entry shows as linked without any extra work here. */}
      {setupCostPriceId && (
        <CostPriceDetailModal
          key={setupCostPriceId}
          costPriceId={setupCostPriceId}
          onClose={() => setSetupCostPriceId(null)}
          costTargets={costTargets}
          defaultWastePercentage={defaultWastePercentage}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmationModal
        isOpen={!!deletingEntry}
        title={t('waste.deleteWaste')}
        message={t('waste.deleteConfirmation')}
        onConfirm={handleDelete}
        onCancel={() => setDeletingEntry(null)}
      />
    </div>
  );
};

export default WasteTrackingPage;
